// SearchComponent.jsx
import React, {useEffect, useRef, useState} from "react";
import {Link, useLocation} from "react-router-dom";

import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";

import StarRoundedIcon from "@mui/icons-material/StarRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import InstagramIcon from "@mui/icons-material/Instagram";
import RoomRoundedIcon from "@mui/icons-material/RoomRounded";

import {
    AppBar,
    Box,
    CircularProgress, Divider, Grid,
    IconButton,
    InputAdornment,
    LinearProgress,
    Paper,
    TextField,
    Toolbar,
    Typography,
} from "@mui/material";
import {BASE_URL_API, MARKET_BASE_URL_API, PRODUCT_BASE_URL_API} from "../Const";
import {BasketComponent} from "./BasketComponent";
import {BackButton} from "./HookUtils";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import {useProductUnits} from "../Repository/useProductUnits";
import ListItem from "@mui/material/ListItem";
import Chip from "@mui/material/Chip";

const CARD_WIDTH = 150;
const DEBOUNCE_MS = 1000;

/* ---------- Memoized Render helpers (no style changes) ---------- */

const ProductCard = React.memo(function ProductCard({ p, units, category }) {
    const MAX_LENGTH = 20;

    const formatStep = (val) => {
        const num = Number(val);
        if (!isFinite(num) || num <= 0) return "";
        return parseFloat(num.toFixed(4)).toLocaleString("en", {
            maximumFractionDigits: 4,
        });
    };

    const formatTomans = (val) =>
        Number(val || 0)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",") + " تومان";

    const unit = units?.find((u) => u.id === p.unit_id);

    return (
        <ListItem
            key={p.id}
            component={Link}
            to={`/product?id=${p.id}`}
            sx={{
                // 🔹 Let flexbox calculate how many cards per row
                flex: "1 1 160px",      // min width ~160px
                maxWidth: 220,          // don’t let it get too wide
                px: 0,
                textDecoration: "none",
                color: "inherit",
            }}
        >
            <Paper
                elevation={1}
                sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 2,
                    p: 1.25,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    transition: "transform 120ms ease, box-shadow 120ms ease",
                    "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 3,
                    },
                }}
            >
                {/* Image */}
                <Box
                    sx={{
                        width: 140,
                        height: 140,
                        borderRadius: 1.5,
                        overflow: "hidden",
                        bgcolor: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        alignSelf: "center",
                    }}
                >
                    <Box
                        component="img"
                        alt={p.title}
                        src={
                            p.pictures && p.pictures.length > 0
                                ? PRODUCT_BASE_URL_API + p.pictures[0].url
                                : "hamekara-logo.png"
                        }
                        loading="lazy"
                        decoding="async"
                        sx={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            display: "block",
                        }}
                    />
                </Box>

                {/* Category inside card */}


                {/* Title */}
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 700,
                        color: "text.primary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        direction: "rtl",
                    }}
                    title={p.title}
                >
                    {p.title.length > MAX_LENGTH
                        ? `${p.title.substring(0, MAX_LENGTH)}...`
                        : p.title}
                </Typography>

                {/* Inventory / تماس بگیرید */}
                <Typography
                    variant="caption"
                    sx={{
                        lineHeight: 1.2,
                        fontWeight: 500,
                        fontSize: "0.82rem",
                        color:
                            Number(p.step) > 0
                                ? "#909090"
                                : "error.main",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        whiteSpace: "nowrap",
                        direction: "rtl",
                        textShadow:
                            Number(p.step) > 0
                                ? "0 0 4px rgba(0,0,0,0.05)"
                                : "0 0 6px rgba(255,0,0,0.3)",
                    }}
                >
                    {Number(p.step) > 0 ? (
                        <>
                            <Inventory2Icon
                                fontSize="inherit"
                                sx={{ opacity: 0.8 }}
                            />
                            موجودی:&nbsp;
                            {formatStep(p.step)} {unit ? unit.name : ""}
                        </>
                    ) : (
                        "تماس بگیرید"
                    )}
                </Typography>

                {/* Chip Group */}
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",   // ✅ allow wrapping
                        gap: 0.5,
                        mt: 0.3,
                        maxWidth: "100%",   // ✅ prevent overflow
                    }}
                >
                    {/* Market chip */}
                    {p.markets?.name && (
                        <Chip
                            label={p.markets.name}
                            size="small"
                            sx={{
                                display: "inline-flex",
                                maxWidth: "100%",    // ✅ keep inside card
                                direction: "rtl",
                                fontSize: 11,
                                height: 22,
                                borderRadius: 1,
                                px: 0.8,
                                bgcolor: "grey.100",
                                color: "text.secondary",
                                "& .MuiChip-label": {
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    px: 0.5,
                                },
                            }}
                            title={p.markets.name}
                        />
                    )}

                    {/* Category chip */}
                    {category && (
                        <Chip
                            label={category}
                            size="small"
                            sx={{
                                display: "inline-flex",
                                maxWidth: "100%",     // ✅ keep inside card
                                direction: "rtl",
                                fontSize: 11,
                                height: 22,
                                borderRadius: 1,
                                px: 0.8,
                                bgcolor: "grey.100",
                                color: "text.secondary",
                                "& .MuiChip-label": {
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    px: 0.5,
                                },
                            }}
                            title={category}
                        />
                    )}
                </Box>


                {/* Price / Discount */}
                {Number(p.del_price) > 0 ? (
                    <Box sx={{ mt: "auto" }}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "text.secondary",
                                textDecoration: "line-through",
                                whiteSpace: "nowrap",
                                direction: "rtl",
                            }}
                        >
                            <strong>{formatTomans(p.price)}</strong>
                        </Typography>

                        <Typography
                            variant="body1"
                            sx={{
                                color: "success.main",
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                                direction: "rtl",
                            }}
                        >
                            <strong>
                                {formatTomans(
                                    parseInt(p.price || 0, 10) -
                                    parseInt(p.del_price || 0, 10)
                                )}
                            </strong>
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ mt: "auto" }}>
                        <Typography
                            variant="body1"
                            sx={{
                                color:
                                    Number(p.price) > 0
                                        ? "success.main"
                                        : "warning.main",
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                                direction: "rtl",
                            }}
                        >
                            <strong>
                                {Number(p.price) > 0
                                    ? formatTomans(p.price)
                                    : "تماس بگیرید."}
                            </strong>
                        </Typography>
                    </Box>
                )}
            </Paper>
        </ListItem>
    );
});

const MarketCard = React.memo(function MarketCard({ m }) {
    const formatTomans = (val) => {
        const n = Number(val || 0);
        if (!isFinite(n) || n <= 0) return "ارسال: نامشخص";
        return `ارسال: ${n.toLocaleString("fa-IR")} تومان`;
    };

    const isFilled = (v) =>
        v !== null &&
        v !== undefined &&
        String(v).trim() !== "" &&
        String(v).toLowerCase() !== "none";

    return (
        <ListItem
            key={m.id}
            component={Link}
            to={
                m.slug && m.slug.trim() !== ""
                    ? `/${encodeURIComponent(m.slug)}`
                    : `/market?id=${m.id}`
            }
            sx={{
                width: 170,
                flexShrink: 0,
                padding: 0,
                textDecoration: "none",
                color: "inherit",
            }}
        >

            <Paper
                elevation={2}
                sx={{
                    width: 150,
                    borderRadius: 2.5,
                    overflow: "hidden",
                    bgcolor: "background.paper",
                    transition: "transform .15s ease, box-shadow .15s ease",
                    "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: 5,
                    },
                }}
            >
                {/* Image */}
                <Box
                    sx={{
                        position: "relative",
                        width: "100%",
                        height: 150,
                        bgcolor: "#f5f5f5",
                    }}
                >
                    <Box
                        component="img"
                        alt={m.name}
                        src={
                            m.pictures?.length
                                ? MARKET_BASE_URL_API + m.pictures[0].url
                                : "hamekara-logo.png"
                        }
                        loading="lazy"
                        decoding="async"
                        sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                        }}
                    />
                </Box>

                {/* Body */}
                <Box sx={{ px: 1, pt: 1, pb: 1.2 }}>
                    {/* Market Name */}
                    <Typography
                        sx={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "text.primary",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            textAlign: "right",
                            direction: "rtl",
                        }}
                        title={m.name}
                    >
                        {m.name}
                    </Typography>

                    {/* Rating + Shipping */}
                    <Box
                        sx={{
                            mt: 0.25,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 0.5,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.3,
                            }}
                        >
                            <StarRoundedIcon
                                sx={{
                                    fontSize: 16,
                                    color: "warning.main",
                                }}
                            />
                            <Typography
                                sx={{ fontSize: 12, color: "text.secondary" }}
                            >
                                {Number(m.rate || 0).toFixed(1)}
                            </Typography>
                        </Box>

                        <Chip
                            label={formatTomans(m.shipping)}
                            size="small"
                            sx={{
                                display: "inline-flex",
                                height: 22,
                                maxWidth: 90,
                                bgcolor: "grey.100",
                                color: "text.secondary",
                                "& .MuiChip-label": {
                                    px: 0.75,
                                    fontSize: 10.5,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                },
                            }}
                            title={formatTomans(m.shipping)}
                        />
                    </Box>

                    {/* Address */}
                    {isFilled(m.address) && (
                        <Typography
                            sx={{
                                mt: 0.5,
                                fontSize: 11,
                                color: "text.secondary",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                textAlign: "right",
                                direction: "rtl",
                            }}
                            title={m.address}
                        >
                            {m.address}
                        </Typography>
                    )}

                    {/* Action Buttons */}
                    <Box
                        sx={{
                            mt: 0.75,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        {/* Call */}
                        {isFilled(m.tell) ? (
                            <IconButton
                                size="small"
                                onClick={(e) => e.stopPropagation()}
                                component="a"
                                href={`tel:${m.tell}`}
                            >
                                <CallRoundedIcon fontSize="small" />
                            </IconButton>
                        ) : (
                            <Box />
                        )}

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                            }}
                        >
                            {/* WhatsApp */}
                            {isFilled(m.whatsapp) && (
                                <IconButton
                                    size="small"
                                    onClick={(e) => e.stopPropagation()}
                                    component="a"
                                    href={`https://wa.me/${String(
                                        m.whatsapp
                                    ).replace(/[^\d]/g, "")}`}
                                    target="_blank"
                                >
                                    <WhatsAppIcon fontSize="small" />
                                </IconButton>
                            )}

                            {/* Instagram */}
                            {isFilled(m.instagram) && (
                                <IconButton
                                    size="small"
                                    onClick={(e) => e.stopPropagation()}
                                    component="a"
                                    href={`https://instagram.com/${String(
                                        m.instagram
                                    ).replace(/^@/, "")}`}
                                    target="_blank"
                                >
                                    <InstagramIcon fontSize="small" />
                                </IconButton>
                            )}

                            {/* Map */}
                            {isFilled(m.lat) && isFilled(m.long) && (
                                <IconButton
                                    size="small"
                                    onClick={(e) => e.stopPropagation()}
                                    component="a"
                                    href={`https://www.google.com/maps?q=${m.lat},${m.long}`}
                                    target="_blank"
                                >
                                    <RoomRoundedIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </ListItem>
    );
});

const Section = React.memo(function Section({title, children}) {
    return (
        <Box sx={{mt: 3}}>
            <Typography sx={{mr: 1, fontWeight: 700}}>{title}</Typography>
            <Box
                sx={{
                    mt: 1,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    justifyContent: "center",
                }}
            >
                {children}
            </Box>
        </Box>
    );
});

/* ---------- Main Component ---------- */

export default function SearchComponent() {
    const [inputValue, setInputValue] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [productGroups, setProductGroups] = useState([]);
    const [marketGroups, setMarketGroups] = useState([]);
    const {units} = useProductUnits();
    const debounceRef = useRef(null);
    const abortRef = useRef(null);

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const preferMarketId = searchParams.get("prefer_market_id") || "";

    /* ---------- Helpers ---------- */
    const groupByCategory = (items) => {
        const map = new Map();

        items.forEach((item) => {
            const catTitle = item.category?.title || "بدون دسته";
            if (!map.has(catTitle)) map.set(catTitle, []);
            map.get(catTitle).push(item);
        });

        let groups = Array.from(map.entries())
            .map(([category, list]) => ({category, list}));

        // ⭐ Keep داغ داغ first
        groups.sort((a, b) => {
            if (a.category.includes("داغ داغ") && !b.category.includes("داغ داغ")) return -1;
            if (!a.category.includes("داغ داغ") && b.category.includes("داغ داغ")) return 1;
            return 0;
        });

        return groups;
    };

    /* ---------- Fetch / Search ---------- */
    const fetchResults = async (q) => {
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setLoading(true);
        try {
            const res = await fetch(
                `${BASE_URL_API}search?q=${encodeURIComponent(q)}&prefer_market_id=${preferMarketId}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("pwa_token")}`,
                        "Content-Type": "application/json",
                    },
                    signal: controller.signal,
                }
            );
            const json = await res.json();
            if (json.code === 200) {
                setProductGroups(groupByCategory(json.data.products || []));
                setMarketGroups(groupByCategory(json.data.markets || []));
            }
        } catch (err) {
            if (err.name !== "AbortError") console.error(err);
        } finally {
            setLoading(false);
        }
    };

    /* ---------- Debounce input -> searchValue (NO length limit) ---------- */
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(() => {
            // we pass exact value (can be "", "a", etc.)
            setSearchValue(inputValue);
        }, DEBOUNCE_MS);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [inputValue]);

    /* ---------- Trigger fetch whenever searchValue changes ---------- */
    useEffect(() => {
        // also fetch for empty string
        fetchResults(searchValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    /* ---------- Render ---------- */

    return (
        <>
            <AppBar sx={{background: "#ffffffcc", boxShadow: "none"}}>
                <Toolbar
                    sx={{
                        bgcolor: "#fff",
                        borderRadius: "0 0 16px 16px",
                        px: 2,
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        position: "sticky",
                        top: 0,
                        zIndex: 20,
                    }}
                >
                    {/* Search */}
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="جستجو کالا و فروشگاه..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                    borderColor: "#ccc",
                                },
                                "&:hover fieldset": {
                                    borderColor: "#d9d9d9",
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: "#d9d9d9 !important",
                                    borderWidth: "2px",
                                },
                            },
                            "& .MuiInputBase-input": {
                                paddingRight: "8px",
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon
                                        sx={{
                                            color: "#000",
                                            fontSize: 26,
                                            "@keyframes searchPulse": {
                                                "0%": {transform: "scale(1)", opacity: 1},
                                                "50%": {transform: "scale(1.08)", opacity: 0.85},
                                                "100%": {transform: "scale(1)", opacity: 1},
                                            },
                                            animation: "searchPulse 1.6s ease-in-out infinite",
                                            filter: "drop-shadow(0 0 4px rgba(0,0,0,0.2))",
                                        }}
                                    />
                                </InputAdornment>
                            ),
                            endAdornment:
                                loading && (
                                    <InputAdornment position="end">
                                        <CircularProgress size={20}/>
                                    </InputAdornment>
                                ),
                        }}
                    />

                    <Box sx={{flexGrow: 1}}/>

                    {/* Basket Button */}
                    <IconButton
                        size="large"
                        sx={{
                            color: "#000",
                            bgcolor: "rgba(0,0,0,0.04)",
                            "&:hover": {bgcolor: "rgba(0,0,0,0.08)"},
                        }}
                    >
                        <BasketComponent/>
                    </IconButton>

                    {/* Back Button */}
                    <Box>
                        <BackButton/>
                    </Box>
                </Toolbar>

                {loading && <LinearProgress color="secondary"/>}
            </AppBar>

            <Box sx={{mt: 10, mb: 8, px: 2}}>
                <Section key={"محصولات"} title={"محصولات"}>

                    {productGroups.map((g) => (
                        g.list.map((p) => (
                            <ProductCard key={p.id} p={p} units={units} category={g.category}/>
                        ))
                    ))}
                </Section>
                <Section key={"فروشگاه‌ها"} title={"فروشگاه‌ها"}>
                    {marketGroups.map((g) => (
                        g.list.map((m) => (
                            <MarketCard key={m.id} m={m}/>
                        ))

                    ))}
                </Section>

                {!loading && !productGroups.length && !marketGroups.length && searchValue && (
                    <Typography sx={{mt: 6, textAlign: "center", color: "text.secondary"}}>
                        نتیجه‌ای یافت نشد.
                    </Typography>
                )}
            </Box>

            <Box
                component="footer"
                sx={{
                    backgroundColor: '#1e1e1e',
                    color: '#f1f1f1',
                    px: 3,
                    py: 4,
                    mt: 6,
                    fontSize: 14,
                    pb: "70px",
                    textAlign: 'center'
                }}
            >
                <Grid container spacing={3} justifyContent="center">
                    <Grid item xs={12} sm={4}>
                        <Typography variant="h6" sx={{fontWeight: 'bold', mb: 1}}>
                            درباره همه کارا
                        </Typography>
                        <Typography variant="body2" sx={{mb: 1}}>
                            سامانه فروشگاهی <strong>همه کارا</strong> راهکاری نوین برای مدیریت فروشگاه‌های
                            آنلاین و
                            آفلاین است. با امکانات ثبت سفارش، باشگاه مشتریان، گزارش‌ گیری مالی و پشتیبانی.
                        </Typography>
                        <a
                            href="https://intro.hamekara.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{color: '#4fc3f7', textDecoration: 'none', fontWeight: 'bold'}}
                        >
                            معرفی کامل همه کارا
                        </a>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Typography variant="h6" sx={{fontWeight: 'bold', mb: 1}}>
                            تماس با ما
                        </Typography>
                        <Typography variant="body2" sx={{mb: 0.5}}>📞 0383-228-5853</Typography>
                        <Typography variant="body2" sx={{mb: 0.5}}>📧 thetoptraders@gmail.com</Typography>
                        <Typography variant="body2"> 📍 ایران - شهرکرد - ساختمان علم و فناوری پردیس - طبقه
                            سوم -
                            واحد 305</Typography>
                    </Grid>

                    <Grid item xs={12} sm={4}
                          sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>

                        <div className="trust-badge">
                            <a
                                referrerPolicy="origin"
                                target="_blank"
                                href="https://trustseal.enamad.ir/?id=618220&Code=W86FGXOVhlD2RNWlNLWwxE5EVujECL5N"
                            >
                                <img
                                    referrerPolicy="origin"
                                    src="https://trustseal.enamad.ir/logo.aspx?id=618220&Code=W86FGXOVhlD2RNWlNLWwxE5EVujECL5N"
                                    alt=""
                                    style={{cursor: "pointer"}}
                                    code="W86FGXOVhlD2RNWlNLWwxE5EVujECL5N"
                                />
                            </a>
                        </div>
                    </Grid>
                </Grid>

                <Divider sx={{my: 3, bgcolor: '#555'}}/>

                <Typography variant="caption" sx={{color: '#aaa'}}>
                    © {new Date().getFullYear()} Hamekara.com - کلیه حقوق محفوظ است
                </Typography>
            </Box>
        </>
    );
}
